import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Cigarette, Sun, Moon, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WeeklyTracking = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    cigarettes_today: 0,
    smoked_morning: null,
    smoked_night: null,
    less_than_yesterday: null,
    more_morning_or_night: null
  });

  const questions = [
    {
      id: 'cigarettes_today',
      title_fr: 'Combien de cigarettes avez-vous fumé aujourd\'hui ?',
      title_en: 'How many cigarettes did you smoke today?',
      type: 'number',
      icon: Cigarette
    },
    {
      id: 'smoked_morning',
      title_fr: 'Avez-vous fumé des cigarettes le matin ?',
      title_en: 'Did you smoke cigarettes in the morning?',
      type: 'boolean',
      icon: Sun
    },
    {
      id: 'smoked_night',
      title_fr: 'Avez-vous fumé des cigarettes la nuit ?',
      title_en: 'Did you smoke cigarettes at night?',
      type: 'boolean',
      icon: Moon
    },
    {
      id: 'less_than_yesterday',
      title_fr: 'Avez-vous moins fumé aujourd\'hui qu\'hier ?',
      title_en: 'Did you smoke less today than yesterday?',
      type: 'boolean',
      icon: TrendingDown
    },
    {
      id: 'more_morning_or_night',
      title_fr: 'Avez-vous fumé le plus de cigarettes le matin ou la nuit ?',
      title_en: 'Did you smoke more cigarettes in the morning or at night?',
      type: 'choice',
      options: [
        { value: 'morning', label_fr: 'Le matin', label_en: 'Morning' },
        { value: 'night', label_fr: 'La nuit', label_en: 'Night' },
        { value: 'equal', label_fr: 'Égal', label_en: 'Equal' }
      ],
      icon: Cigarette
    }
  ];

  // Calcul automatique de l'envie (craving) basé sur les réponses
  const calculateCraving = () => {
    let score = 0;
    
    // Question 1: Nombre de cigarettes (0-10 points)
    // Plus de cigarettes = plus d'envie
    const cigs = formData.cigarettes_today;
    if (cigs <= 5) score += 2;
    else if (cigs <= 10) score += 4;
    else if (cigs <= 15) score += 6;
    else if (cigs <= 20) score += 8;
    else score += 10;
    
    // Question 2: Fumé le matin (+3 points si oui)
    // Fumer le matin = signe de forte dépendance/envie
    if (formData.smoked_morning === true) score += 3;
    
    // Question 3: Fumé la nuit (+2 points si oui)
    if (formData.smoked_night === true) score += 2;
    
    // Question 4: Moins fumé qu'hier (-2 points si oui)
    // Réduction = moins d'envie
    if (formData.less_than_yesterday === true) score -= 2;
    
    // Question 5: Plus le matin que la nuit (+1 point)
    // Fumer plus le matin = dépendance plus forte
    if (formData.more_morning_or_night === 'morning') score += 1;
    
    // Normaliser entre 1 et 10
    return Math.max(1, Math.min(10, Math.round(score)));
  };

  // Calcul automatique de l'anxiété basé sur les réponses
  const calculateAnxiety = () => {
    let score = 0;
    
    // Question 1: Nombre de cigarettes (0-10 points)
    // Plus de cigarettes peut indiquer plus d'anxiété
    const cigs = formData.cigarettes_today;
    if (cigs <= 5) score += 1;
    else if (cigs <= 10) score += 3;
    else if (cigs <= 15) score += 5;
    else if (cigs <= 20) score += 7;
    else score += 9;
    
    // Question 2: Fumé le matin (+1 point si oui)
    if (formData.smoked_morning === true) score += 1;
    
    // Question 3: Fumé la nuit (+4 points si oui)
    // Fumer la nuit = souvent lié à l'anxiété/insomnie
    if (formData.smoked_night === true) score += 4;
    
    // Question 4: Moins fumé qu'hier (-2 points si oui)
    // Réduction = moins d'anxiété
    if (formData.less_than_yesterday === true) score -= 2;
    
    // Question 5: Plus la nuit que le matin (+2 points)
    // Fumer plus la nuit = anxiété nocturne
    if (formData.more_morning_or_night === 'night') score += 2;
    
    // Normaliser entre 1 et 10
    return Math.max(1, Math.min(10, Math.round(score)));
  };

  // Calcul de l'humeur (inverse de l'anxiété + bonus si réduction)
  const calculateMood = () => {
    let score = 10; // Commencer à 10 et soustraire
    
    // Moins de cigarettes = meilleure humeur
    const cigs = formData.cigarettes_today;
    if (cigs > 20) score -= 4;
    else if (cigs > 15) score -= 3;
    else if (cigs > 10) score -= 2;
    else if (cigs > 5) score -= 1;
    
    // Fumé la nuit = moins bien dormi = moins bonne humeur
    if (formData.smoked_night === true) score -= 2;
    
    // Moins fumé qu'hier = fierté = meilleure humeur
    if (formData.less_than_yesterday === true) score += 2;
    
    // Normaliser entre 1 et 10
    return Math.max(1, Math.min(10, Math.round(score)));
  };

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;
  const isLastStep = currentStep === questions.length - 1;

  const handleNext = () => {
    const currentValue = formData[currentQuestion.id];
    
    if (currentQuestion.type === 'boolean' && currentValue === null) {
      toast.error(language === 'fr' ? 'Veuillez répondre à la question' : 'Please answer the question');
      return;
    }
    
    if (currentQuestion.type === 'choice' && currentValue === null) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner une option' : 'Please select an option');
      return;
    }

    if (isLastStep) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const today = new Date();
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      
      // Calculer automatiquement les scores
      const cravingLevel = calculateCraving();
      const anxietyLevel = calculateAnxiety();
      const moodLevel = calculateMood();
      
      await axios.post(`${API_URL}/tracking/weekly`, {
        week_start_date: weekStart.toISOString().split('T')[0],
        cigarettes_per_day: formData.cigarettes_today,
        craving_level: cravingLevel,
        stress_level: anxietyLevel,
        mood_level: moodLevel,
        smoked_morning: formData.smoked_morning,
        smoked_night: formData.smoked_night,
        less_than_yesterday: formData.less_than_yesterday,
        more_morning_or_night: formData.more_morning_or_night,
        notes: `Envie: ${cravingLevel}/10, Anxiété: ${anxietyLevel}/10, Humeur: ${moodLevel}/10 (calculés automatiquement)`
      });

      toast.success(language === 'fr' ? 'Suivi enregistré avec succès !' : 'Tracking saved successfully!');
      navigate('/statistics');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'enregistrement' : 'Error saving tracking');
    } finally {
      setLoading(false);
    }
  };

  const renderQuestionContent = () => {
    switch (currentQuestion.type) {
      case 'number':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-center">
              <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.cigarettes_today}
                  onChange={(e) => setFormData(prev => ({ ...prev, cigarettes_today: parseInt(e.target.value) || 0 }))}
                  className="w-20 h-20 text-center text-4xl font-bold border-0 bg-transparent focus:ring-0"
                  data-testid="cigarettes-input"
                />
              </div>
            </div>
            <p className="text-center text-gray-500">
              {language === 'fr' ? 'cigarettes' : 'cigarettes'}
            </p>
          </div>
        );
        
      case 'boolean':
        return (
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => setFormData(prev => ({ ...prev, [currentQuestion.id]: true }))}
              className={`flex-1 max-w-[150px] p-6 rounded-2xl border-2 transition-all ${
                formData[currentQuestion.id] === true 
                  ? 'border-primary bg-primary/10 shadow-lg' 
                  : 'border-gray-200 hover:border-primary/30'
              }`}
              data-testid="option-yes"
            >
              <div className="text-center">
                <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 ${
                  formData[currentQuestion.id] === true ? 'bg-primary text-white' : 'bg-gray-100'
                }`}>
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <span className="font-semibold text-lg">
                  {language === 'fr' ? 'Oui' : 'Yes'}
                </span>
              </div>
            </button>
            
            <button
              onClick={() => setFormData(prev => ({ ...prev, [currentQuestion.id]: false }))}
              className={`flex-1 max-w-[150px] p-6 rounded-2xl border-2 transition-all ${
                formData[currentQuestion.id] === false 
                  ? 'border-primary bg-primary/10 shadow-lg' 
                  : 'border-gray-200 hover:border-primary/30'
              }`}
              data-testid="option-no"
            >
              <div className="text-center">
                <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 ${
                  formData[currentQuestion.id] === false ? 'bg-primary text-white' : 'bg-gray-100'
                }`}>
                  <span className="text-2xl">✕</span>
                </div>
                <span className="font-semibold text-lg">
                  {language === 'fr' ? 'Non' : 'No'}
                </span>
              </div>
            </button>
          </div>
        );
        
      case 'choice':
        return (
          <div className="space-y-3">
            {currentQuestion.options.map((option) => (
              <button
                key={option.value}
                onClick={() => setFormData(prev => ({ ...prev, [currentQuestion.id]: option.value }))}
                className={`w-full p-4 rounded-2xl border-2 text-left transition-all flex items-center gap-4 ${
                  formData[currentQuestion.id] === option.value 
                    ? 'border-primary bg-primary/10 shadow-lg' 
                    : 'border-gray-200 hover:border-primary/30'
                }`}
                data-testid={`option-${option.value}`}
              >
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  formData[currentQuestion.id] === option.value 
                    ? 'border-primary bg-primary' 
                    : 'border-gray-300'
                }`}>
                  {formData[currentQuestion.id] === option.value && (
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  )}
                </div>
                <span className="font-medium text-lg">
                  {language === 'fr' ? option.label_fr : option.label_en}
                </span>
              </button>
            ))}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="weekly-tracking">
      <div className="max-w-2xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8 animate-fade-in-up">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              {language === 'fr' ? 'Question' : 'Question'} {currentStep + 1} {language === 'fr' ? 'sur' : 'of'} {questions.length}
            </span>
            <span className="text-sm font-medium text-primary">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-2 bg-gray-200" />
        </div>

        {/* Question Card */}
        <div className="glass-card rounded-3xl p-8 sm:p-10 shadow-xl animate-fade-in-up" data-testid="tracking-card">
          {/* Question Number Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <span className="text-sm font-semibold text-primary">
              {language === 'fr' ? 'Suivi hebdomadaire' : 'Weekly tracking'}
            </span>
          </div>

          {/* Question Text */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-8 leading-relaxed">
            {language === 'fr' ? currentQuestion.title_fr : currentQuestion.title_en}
          </h2>

          {/* Question Content */}
          <div className="mb-8">
            {renderQuestionContent()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-100">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="rounded-full"
              data-testid="prev-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Précédent' : 'Previous'}
            </Button>

            <Button
              onClick={handleNext}
              disabled={loading}
              className="rounded-full bg-primary hover:bg-primary/90 shadow-lg px-8"
              data-testid="next-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isLastStep ? (
                <>
                  {language === 'fr' ? 'Terminer' : 'Finish'}
                  <CheckCircle2 className="ml-2 w-4 h-4" />
                </>
              ) : (
                <>
                  {language === 'fr' ? 'Suivant' : 'Next'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Progress Dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {questions.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentStep 
                  ? 'w-6 bg-primary' 
                  : index < currentStep
                    ? 'bg-primary/60'
                    : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Info Box */}
        <div className="mt-8 p-4 bg-blue-50 rounded-2xl text-center animate-fade-in-up">
          <p className="text-sm text-blue-700">
            {language === 'fr' 
              ? 'Votre niveau d\'envie et d\'anxiété sera calculé automatiquement à partir de vos réponses.'
              : 'Your craving and anxiety levels will be automatically calculated from your answers.'}
          </p>
        </div>
      </div>
    </div>
  );
};
